import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { useAuth } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Filter, AlertTriangle, Clock, Lock,
  Image as ImageIcon, Video, Settings as SettingsIcon, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { key: 'planned',          label: 'Planned',          color: 'bg-slate-500',    types: ['static', 'video'] },
  { key: 'content_written',  label: 'Content Written',  color: 'bg-blue-500',     types: ['static', 'video'] },
  { key: 'video_shot',       label: 'Video Shot',       color: 'bg-cyan-500',     types: ['video'] },
  { key: 'video_edited',     label: 'Video Edited',     color: 'bg-indigo-500',   types: ['video'] },
  { key: 'design_ready',     label: 'Design Ready',     color: 'bg-fuchsia-500',  types: ['static'] },
  { key: 'pending_approval', label: 'Pending Approval', color: 'bg-amber-500',    types: ['static', 'video'] },
  { key: 'approved',         label: 'Approved',         color: 'bg-emerald-500',  types: ['static', 'video'] },
  { key: 'scheduled',        label: 'Scheduled',        color: 'bg-violet-600',   types: ['static', 'video'] },
];

const RANGE_PRESETS = [
  { key: 'today',      label: 'Today' },
  { key: 'this_week',  label: 'This Week' },
  { key: 'next_week',  label: 'Next Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'custom',     label: 'Custom' },
];

const todayStr = () => new Date().toISOString().split('T')[0];
const addDays = (d, days) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x.toISOString().split('T')[0];
};
const startOfWeek = (d = new Date()) => {
  const x = new Date(d);
  const day = x.getDay();
  const diff = x.getDate() - day;
  return new Date(x.setDate(diff)).toISOString().split('T')[0];
};

export default function MarketingCalendarPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('this_week');
  const [customStart, setCustomStart] = useState(todayStr());
  const [customEnd, setCustomEnd] = useState(addDays(todayStr(), 7));
  const [stageFilter, setStageFilter] = useState('all');
  const [openEntry, setOpenEntry] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { startDate, endDate } = useMemo(() => {
    const t = todayStr();
    if (range === 'today') return { startDate: t, endDate: t };
    if (range === 'this_week') return { startDate: startOfWeek(), endDate: addDays(startOfWeek(), 6) };
    if (range === 'next_week') {
      const nws = addDays(startOfWeek(), 7);
      return { startDate: nws, endDate: addDays(nws, 6) };
    }
    if (range === 'this_month') {
      const d = new Date();
      const start = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0];
      return { startDate: start, endDate: end };
    }
    return { startDate: customStart, endDate: customEnd };
  }, [range, customStart, customEnd]);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/marketing/calendar/v2?start_date=${startDate}&end_date=${endDate}`);
      setEntries(r.data?.entries || []);
    } catch { toast.error('Failed to load calendar'); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadEntries(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  useEffect(() => {
    const id = searchParams.get('entry');
    if (id && entries.length > 0) {
      const found = entries.find(e => e.id === id);
      if (found) setOpenEntry(found);
    }
  }, [searchParams, entries]);

  const role = user?.role || '';
  const canManage = ['super_admin', 'admin', 'coo', 'marketing', 'marketing_admin'].includes(role);
  const canApprove = ['super_admin', 'admin', 'coo', 'marketing'].includes(role);
  const canSchedule = ['super_admin', 'admin', 'coo', 'marketing_admin'].includes(role);

  const visibleStages = useMemo(() => {
    if (stageFilter !== 'all') return STAGES.filter(s => s.key === stageFilter);
    return STAGES;
  }, [stageFilter]);

  const grouped = useMemo(() => {
    const m = {}; for (const s of STAGES) m[s.key] = [];
    for (const e of entries) (m[e.status] || m.planned).push(e);
    return m;
  }, [entries]);

  return (
    <div className="space-y-4 p-4" data-testid="marketing-calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Content Calendar</h1>
          <p className="text-sm text-muted-foreground">
            Kanban workflow · {entries.length} cards in view
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-[140px]" data-testid="date-range-select">
              <Filter className="h-3.5 w-3.5 mr-1" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_PRESETS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {range === 'custom' && (
            <>
              <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="w-[150px]" />
              <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="w-[150px]" />
            </>
          )}
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-[180px]" data-testid="stage-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadEntries}><RefreshCw className="h-4 w-4" /></Button>
          {canManage && (
            <>
              <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="create-entry-btn">
                + New Entry
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="open-settings-btn">
                <SettingsIcon className="h-4 w-4 mr-1" /> Settings
              </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20"><RefreshCw className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="grid grid-flow-col auto-cols-[280px] gap-3" data-testid="kanban-board">
            {visibleStages.map(stage => (
              <KanbanColumn key={stage.key} stage={stage} cards={grouped[stage.key] || []} onCardClick={(e) => setOpenEntry(e)} />
            ))}
          </div>
        </div>
      )}

      <CardDetailModal entry={openEntry} onClose={() => setOpenEntry(null)} onChange={() => { loadEntries(); setOpenEntry(null); }}
        userRole={role} canApprove={canApprove} canSchedule={canSchedule} />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <CreateEntryModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={loadEntries} />
    </div>
  );
}

function CreateEntryModal({ open, onClose, onCreated }) {
  const [pages, setPages] = useState([]);
  const [pageId, setPageId] = useState('');
  const [date, setDate] = useState(todayStr());
  const [title, setTitle] = useState('');
  const [postType, setPostType] = useState('static');
  const [brief, setBrief] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/marketing/calendar/v2/pages').then(r => setPages(r.data || [])).catch(() => {});
    }
  }, [open]);

  const submit = async () => {
    if (!pageId || !date) { toast.error('Page + date required'); return; }
    try {
      setSaving(true);
      await api.post('/marketing/calendar/v2/entries', {
        page_id: pageId, date, title, post_type: postType, content_brief: brief,
      });
      toast.success('Entry created');
      onCreated(); onClose();
      setTitle(''); setBrief('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="create-entry-modal">
        <DialogHeader>
          <DialogTitle>New Calendar Entry</DialogTitle>
          <DialogDescription>Drops as <Badge variant="outline">Planned</Badge> for the Content Writer to pick up.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Page *</Label>
            <Select value={pageId} onValueChange={setPageId}>
              <SelectTrigger data-testid="create-page-select"><SelectValue placeholder="Select page..." /></SelectTrigger>
              <SelectContent>
                {pages.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.platform})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Post Date *</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="create-date" />
            </div>
            <div className="space-y-1">
              <Label>Post Type</Label>
              <Select value={postType} onValueChange={setPostType}>
                <SelectTrigger data-testid="create-post-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="static">Static Post</SelectItem>
                  <SelectItem value="video">Video Content</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Title</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Forex Hook Reel" />
          </div>
          <div className="space-y-1">
            <Label>Initial Brief (optional)</Label>
            <Textarea rows={3} value={brief} onChange={e => setBrief(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="create-submit-btn">
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KanbanColumn({ stage, cards, onCardClick }) {
  return (
    <div className="bg-muted/30 rounded-lg p-2 min-h-[400px]">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${stage.color}`} />
          <span className="text-sm font-semibold">{stage.label}</span>
        </div>
        <Badge variant="secondary" className="h-5 text-[10px]">{cards.length}</Badge>
      </div>
      <div className="space-y-2">
        {cards.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No cards</p>}
        {cards.map(c => <KanbanCard key={c.id} card={c} onClick={() => onCardClick(c)} />)}
      </div>
    </div>
  );
}

function KanbanCard({ card, onClick }) {
  const isVideo = (card.post_type || '').toLowerCase() === 'video';
  const today = new Date();
  let daysToDeadline = null, dueLabel = '';
  if (card.date) {
    const d = new Date(card.date);
    const diff = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
    daysToDeadline = diff;
    dueLabel = diff === 0 ? 'Today' : (diff < 0 ? `${-diff}d overdue` : `${diff}d`);
  }
  const sev = daysToDeadline === null ? 'ok' : (daysToDeadline < 0 ? 'overdue' : (daysToDeadline <= 2 ? 'soon' : 'ok'));
  const Icon = isVideo ? Video : ImageIcon;
  return (
    <div onClick={onClick}
      className="bg-card border rounded p-2 cursor-pointer hover:border-primary transition-colors"
      data-testid={`calendar-card-${card.id}`}>
      <div className="flex items-start gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-muted-foreground" />
        <p className="text-sm font-medium leading-tight line-clamp-2 flex-1">{card.title || '(untitled)'}</p>
        {card.locked && <Lock className="h-3 w-3 text-violet-600 flex-shrink-0" />}
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {card.platform && <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">{card.platform}</Badge>}
        {card.post_type && <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">{card.post_type === 'video' ? 'Video' : 'Static'}</Badge>}
      </div>
      {card.page_name && <p className="text-[11px] text-muted-foreground mt-1 truncate">{card.page_name}</p>}
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-muted-foreground">{card.date}</span>
        {dueLabel && (
          <span className={`text-[10px] flex items-center gap-0.5 ${
            sev === 'overdue' ? 'text-red-500' : sev === 'soon' ? 'text-yellow-500' : 'text-muted-foreground'
          }`}>
            {sev === 'overdue' && <AlertTriangle className="h-2.5 w-2.5" />}
            {sev === 'soon' && <Clock className="h-2.5 w-2.5" />}
            {dueLabel}
          </span>
        )}
      </div>
    </div>
  );
}

function CardDetailModal({ entry, onClose, onChange, userRole, canSchedule }) {
  const [comment, setComment] = useState('');
  const [contentBrief, setContentBrief] = useState('');
  const [postType, setPostType] = useState('static');
  const [scheduleAt, setScheduleAt] = useState('');
  const [editorId, setEditorId] = useState('');
  const [editorDue, setEditorDue] = useState('');
  const [editors, setEditors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (entry) {
      setContentBrief(entry.content_brief || entry.description || '');
      setPostType(entry.post_type || 'static');
      setScheduleAt(entry.scheduled_at || (entry.date ? `${entry.date}T10:00` : ''));
      setEditorId(entry.video_editor_id_assignment || '');
      setEditorDue(entry.video_editor_due_date || '');
      setComment('');
    }
  }, [entry]);

  useEffect(() => {
    if (entry?.post_type === 'video' && ['marketing', 'marketing_admin', 'super_admin', 'admin'].includes(userRole)) {
      api.get('/marketing/calendar/v2/video-editors').then(r => setEditors(r.data || [])).catch(() => {});
    }
  }, [entry, userRole]);

  if (!entry) return null;
  const stage = entry.status || 'planned';
  const isVideo = (entry.post_type || postType) === 'video';
  const transitions = {
    content_writer: stage === 'planned' ? [{ to: 'content_written', label: 'Content Written →', primary: true }] : [],
    videographer: stage === 'content_written' && isVideo ? [{ to: 'video_shot', label: 'Video Shot →', primary: true }] : [],
    video_editor: stage === 'video_shot' && isVideo ? [{ to: 'video_edited', label: 'Video Edited →', primary: true }] : [],
    graphic_designer: stage === 'content_written' && !isVideo ? [{ to: 'design_ready', label: 'Design Ready →', primary: true }] : [],
    marketing: ['design_ready', 'video_edited', 'pending_approval'].includes(stage) ? [
      { to: 'approved', label: 'Approve ✓', primary: true },
      { to: 'rejected', label: 'Reject ✗', danger: true },
    ] : [],
    marketing_admin: stage === 'approved' ? [{ to: 'scheduled', label: 'Schedule →', primary: true }] : [],
  };
  const privileged = ['super_admin', 'admin', 'coo'].includes(userRole);
  let availableActions = transitions[userRole] || [];
  if (privileged) {
    availableActions = [
      ...transitions.content_writer, ...transitions.videographer, ...transitions.video_editor,
      ...transitions.graphic_designer, ...transitions.marketing, ...transitions.marketing_admin,
    ].filter((a, i, self) => self.findIndex(x => x.to === a.to) === i);
  }

  const submitTransition = async (toStage, isReject = false) => {
    if (toStage === 'scheduled' && !scheduleAt) { toast.error('Pick a schedule date/time'); return; }
    if (isReject && !comment.trim()) { toast.error('Rejection requires a comment'); return; }
    try {
      setSubmitting(true);
      const body = { to_stage: toStage, comment };
      if (toStage === 'content_written' && contentBrief) body.content_brief = contentBrief;
      if (toStage === 'scheduled') body.scheduled_at = scheduleAt;
      await api.post(`/marketing/calendar/v2/${entry.id}/transition`, body);
      toast.success(`Card moved to ${toStage.replace('_', ' ')}`);
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSubmitting(false); }
  };

  const assignEditor = async () => {
    if (!editorId || !editorDue) { toast.error('Pick an editor and due date'); return; }
    try {
      await api.put(`/marketing/calendar/v2/${entry.id}/assign-editor`, { editor_id: editorId, due_date: editorDue });
      toast.success('Editor assigned'); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const updatePostType = async (val) => {
    try {
      await api.put(`/marketing/calendar/entries/${entry.id}`, { post_type: val });
      setPostType(val); toast.success('Post type updated');
    } catch { toast.error('Failed'); }
  };

  return (
    <Dialog open={!!entry} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="card-detail-modal">
        <DialogHeader>
          <div className="flex items-start gap-2">
            <DialogTitle className="flex-1">{entry.title || '(untitled post)'}</DialogTitle>
            <Badge>{stage.replace('_', ' ')}</Badge>
          </div>
          <DialogDescription>{entry.platform} · {entry.page_name} · Scheduled {entry.date}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Post Type</Label>
              {['marketing', 'marketing_admin', 'super_admin', 'admin', 'coo'].includes(userRole) ? (
                <Select value={postType} onValueChange={updatePostType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="static">Static Post</SelectItem>
                    <SelectItem value="video">Video Content</SelectItem>
                  </SelectContent>
                </Select>
              ) : <Badge variant="outline">{postType === 'video' ? 'Video' : 'Static'}</Badge>}
            </div>
            <div className="space-y-1">
              <Label>Post Date</Label>
              <p className="text-sm">{entry.date}</p>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Content Brief / Caption</Label>
            <Textarea
              rows={5} value={contentBrief}
              onChange={e => setContentBrief(e.target.value)}
              placeholder="Write the caption / script / brief..."
              disabled={!['planned', 'content_written'].includes(stage) || !['content_writer', 'marketing', 'marketing_admin', 'super_admin', 'admin', 'coo'].includes(userRole)}
              data-testid="content-brief-input" />
          </div>

          {entry.history?.length > 0 && (
            <div className="border rounded p-2 bg-muted/40 space-y-1 max-h-[120px] overflow-y-auto">
              <p className="text-xs font-semibold mb-1">Activity</p>
              {entry.history.slice().reverse().map((h, i) => (
                <p key={i} className="text-[11px] text-muted-foreground">
                  • {h.from} → {h.to} by {h.by_name} ({h.by_role}) {h.comment && `— "${h.comment}"`}
                </p>
              ))}
            </div>
          )}

          {entry.rejected_reason && (
            <div className="border-l-2 border-red-500 pl-2 py-1 text-sm">
              <p className="font-semibold text-red-600">Rejected:</p>
              <p>{entry.rejected_reason}</p>
            </div>
          )}

          {['marketing', 'marketing_admin', 'super_admin', 'admin'].includes(userRole) && stage === 'video_shot' && isVideo && (
            <div className="border rounded p-3 space-y-2 bg-muted/20">
              <p className="text-sm font-semibold">Assign Video Editor</p>
              <div className="grid grid-cols-2 gap-2">
                <Select value={editorId} onValueChange={setEditorId}>
                  <SelectTrigger data-testid="editor-select"><SelectValue placeholder="Select editor..." /></SelectTrigger>
                  <SelectContent>
                    {editors.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name || e.name} ({e.role})</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={editorDue} onChange={e => setEditorDue(e.target.value)} />
              </div>
              <Button size="sm" onClick={assignEditor} data-testid="assign-editor-btn">Assign</Button>
            </div>
          )}

          {stage === 'approved' && canSchedule && (
            <div className="space-y-1">
              <Label>Schedule At</Label>
              <Input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} data-testid="schedule-at-input" />
            </div>
          )}

          <div className="space-y-1">
            <Label>Comment (required for reject)</Label>
            <Input value={comment} onChange={e => setComment(e.target.value)} placeholder="Optional comment" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {availableActions.map(a => (
            <Button key={a.to} variant={a.danger ? 'destructive' : 'default'}
              onClick={() => submitTransition(a.to, a.danger)}
              disabled={submitting}
              data-testid={`transition-${a.to}-btn`}>
              {a.label}
            </Button>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsModal({ open, onClose }) {
  const [shoot, setShoot] = useState(2);
  const [design, setDesign] = useState(1);
  const [sla, setSla] = useState(24);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/marketing/calendar/v2/settings').then(r => {
        setShoot(r.data?.shoot_buffer_days ?? 2);
        setDesign(r.data?.design_buffer_days ?? 1);
        setSla(r.data?.approval_sla_hours ?? 24);
      }).catch(() => {});
    }
  }, [open]);

  const save = async () => {
    try {
      setSaving(true);
      await api.put('/marketing/calendar/v2/settings', {
        shoot_buffer_days: Number(shoot),
        design_buffer_days: Number(design),
        approval_sla_hours: Number(sla),
      });
      toast.success('Settings saved'); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="settings-modal">
        <DialogHeader>
          <DialogTitle>Calendar Settings</DialogTitle>
          <DialogDescription>Buffer days control how far before the post date downstream tasks are due.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Video Shoot Buffer (days before post date)</Label>
            <Input type="number" min="0" max="30" value={shoot} onChange={e => setShoot(e.target.value)} data-testid="shoot-buffer-input" />
          </div>
          <div className="space-y-1">
            <Label>Graphic Design Buffer (days before post date)</Label>
            <Input type="number" min="0" max="30" value={design} onChange={e => setDesign(e.target.value)} data-testid="design-buffer-input" />
          </div>
          <div className="space-y-1">
            <Label>Manager Approval SLA (hours)</Label>
            <Input type="number" min="1" max="168" value={sla} onChange={e => setSla(e.target.value)} data-testid="approval-sla-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-settings-btn">{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
